/**
 * ImportContourUtils.js
 *
 * Utilities for importing a single fire contour polygon from GeoJSON and KML files.
 * All user-facing error messages are translated via i18n
 * (keys under `mapControls.importErrors.*`).
 *
 * Install:
 *   npm install proj4-fully-loaded
 */

import proj4 from "proj4-fully-loaded";
import i18n from "../../i18n/i18n";

// ---------------------------------------------------------------------------
// i18n helper
// ---------------------------------------------------------------------------

/**
 * Build an Error whose message is translated at throw time.
 * @param {string} key      key under mapControls.importErrors
 * @param {object} options  interpolation values, e.g. { epsg, message }
 */
const importError = (key, options = {}) =>
  new Error(i18n.t(`mapControls.importErrors.${key}`, options));

// ---------------------------------------------------------------------------
// CRS detection
// ---------------------------------------------------------------------------

const epsgFromCrsMember = (json) => {
  const crs = json?.crs;
  if (!crs) return null;
  if (crs.type === "name") {
    const name = crs.properties?.name || "";
    const match = name.match(/EPSG[::]+(\d+)/i);
    if (match) return `EPSG:${match[1]}`;
  }
  return null;
};

const guessEpsgFromCoordinates = (coord) => {
  const [x, y] = coord;

  // Lambert-93
  if (x > 100_000 && x < 1_300_000 && y > 6_000_000 && y < 7_200_000) {
    return "EPSG:2154";
  }

  // Web Mercator
  if (
    Math.abs(x) > 180 &&
    Math.abs(x) <= 20_100_000 &&
    Math.abs(y) > 90 &&
    Math.abs(y) <= 20_100_000
  ) {
    return "EPSG:3857";
  }

  // Legacy NTF Lambert zones
  if (x > 50_000 && x < 1_100_000 && y > 1_000_000 && y < 1_400_000) {
    return "EPSG:27561";
  }
  if (x > 50_000 && x < 1_100_000 && y > 2_000_000 && y < 2_400_000) {
    return "EPSG:27562";
  }
  if (x > 50_000 && x < 1_100_000 && y > 3_000_000 && y < 3_400_000) {
    return "EPSG:27563";
  }
  if (x > 50_000 && x < 1_100_000 && y > 4_100_000 && y < 4_300_000) {
    return "EPSG:27564";
  }

  // Looks like UTM but zone can't be determined without a crs member
  if (x > 100_000 && x < 900_000 && y > 0 && y < 10_100_000) {
    return "UNKNOWN_UTM";
  }

  return null;
};

const detectEpsg = (json, firstCoord) => {
  const fromMember = epsgFromCrsMember(json);
  if (fromMember) return fromMember;

  if (firstCoord) {
    const fromHeuristic = guessEpsgFromCoordinates(firstCoord);
    if (fromHeuristic) return fromHeuristic;
  }

  return "EPSG:4326";
};

// ---------------------------------------------------------------------------
// Coordinate reprojection
// ---------------------------------------------------------------------------

const reprojectCoord = (coord, srcEpsg) => {
  if (srcEpsg === "EPSG:4326") return coord;
  const [lon, lat] = proj4(srcEpsg, "EPSG:4326", [coord[0], coord[1]]);
  return [lon, lat];
};

const reprojectPolygonGeometry = (geometry, srcEpsg) => {
  if (srcEpsg === "EPSG:4326") return geometry;
  return {
    ...geometry,
    coordinates: geometry.coordinates.map((ring) =>
      ring.map((coord) => reprojectCoord(coord, srcEpsg)),
    ),
  };
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Parse a GeoJSON (.geojson / .json) file containing a single polygon.
 * @param   {File} file
 * @returns {Promise<{ features, wasReprojected, detectedEpsg }>}
 */
export const parseGeoJSONFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);

        const rawFeatures = extractPolygonFeatures(json);
        if (rawFeatures.length === 0) {
          reject(importError("noPolygonGeoJSON"));
          return;
        }

        // Only a single polygon is allowed
        if (rawFeatures.length > 1) {
          reject(importError("multiPolygon"));
          return;
        }

        const firstCoord = rawFeatures[0].geometry.coordinates[0]?.[0];
        const detectedEpsg = detectEpsg(json, firstCoord);

        if (detectedEpsg === "UNKNOWN_UTM") {
          reject(importError("unknownUtm"));
          return;
        }

        const wasReprojected = detectedEpsg !== "EPSG:4326";

        const features = [];
        for (const rawFeature of rawFeatures) {
          let reprojectedGeometry;
          try {
            reprojectedGeometry = reprojectPolygonGeometry(
              rawFeature.geometry,
              detectedEpsg,
            );
          } catch (projErr) {
            reject(
              importError("reprojectionFailed", {
                epsg: detectedEpsg,
                message: projErr.message,
              }),
            );
            return;
          }

          const feature = normaliseFeature({
            ...rawFeature,
            geometry: reprojectedGeometry,
          });

          const invalidRing = feature.geometry.coordinates.find(
            (ring) => ring.length < 4,
          );
          if (invalidRing) {
            reject(importError("invalidRingAfterReprojection"));
            return;
          }

          features.push(feature);
        }

        resolve({ features, wasReprojected, detectedEpsg });
      } catch {
        reject(importError("invalidGeoJSON"));
      }
    };

    reader.onerror = () => reject(importError("readError"));
    reader.readAsText(file);
  });
};

/**
 * Parse a KML (.kml) file containing a single polygon.
 * KML always uses EPSG:4326 by spec, so no reprojection is needed.
 * @param   {File} file
 * @returns {Promise<{ features, wasReprojected, detectedEpsg }>}
 */
export const parseKMLFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const parser = new DOMParser();
        const kmlDoc = parser.parseFromString(e.target.result, "text/xml");

        const parseError = kmlDoc.querySelector("parsererror");
        if (parseError) {
          reject(importError("invalidKML"));
          return;
        }

        const features = extractPolygonsFromKML(kmlDoc);
        if (features.length === 0) {
          reject(importError("noPolygonKML"));
          return;
        }

        // Only a single polygon is allowed
        if (features.length > 1) {
          reject(importError("multiPolygon"));
          return;
        }

        for (const feature of features) {
          const invalidRing = feature.geometry.coordinates.find(
            (ring) => ring.length < 4,
          );
          if (invalidRing) {
            reject(importError("invalidRingKML"));
            return;
          }
        }

        resolve({ features, wasReprojected: false, detectedEpsg: "EPSG:4326" });
      } catch (err) {
        reject(importError("kmlProcessing", { message: err.message }));
      }
    };

    reader.onerror = () => reject(importError("readError"));
    reader.readAsText(file);
  });
};

/**
 * Main entry point. Detects file type and delegates to the right parser.
 * @param   {File} file
 * @returns {Promise<{ features, wasReprojected, detectedEpsg }>}
 */
export const parseContourFile = (file) => {
  const name = file.name.toLowerCase();

  if (name.endsWith(".geojson") || name.endsWith(".json")) {
    return parseGeoJSONFile(file);
  }
  if (name.endsWith(".kml")) {
    return parseKMLFile(file);
  }
  return Promise.reject(importError("unsupportedFormat"));
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Walk any GeoJSON structure and return ALL Polygon features found
 * (a MultiPolygon yields one feature per part). The count is used by the
 * caller to reject files containing more than one polygon.
 */
const extractPolygonFeatures = (json) => {
  const candidates = [];

  if (json.type === "FeatureCollection") {
    candidates.push(...(json.features || []));
  } else if (json.type === "Feature") {
    candidates.push(json);
  } else if (json.type === "Polygon" || json.type === "MultiPolygon") {
    candidates.push({ type: "Feature", geometry: json, properties: {} });
  }

  const results = [];

  for (const f of candidates) {
    const geom = f.geometry;
    if (!geom) continue;

    if (geom.type === "Polygon") {
      results.push(normaliseFeature(f));
    }

    if (geom.type === "MultiPolygon") {
      geom.coordinates.forEach((polygonRings) => {
        results.push(
          normaliseFeature({
            type: "Feature",
            properties: f.properties || {},
            geometry: { type: "Polygon", coordinates: polygonRings },
          }),
        );
      });
    }
  }

  return results;
};

const normaliseFeature = (feature) => {
  const coords = feature.geometry.coordinates.map((ring) => {
    const deduped = ring.filter(
      (coord, i) =>
        i === 0 ||
        coord[0] !== ring[i - 1][0] ||
        coord[1] !== ring[i - 1][1],
    );

    const first = deduped[0];
    const last = deduped[deduped.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      deduped.push([...first]);
    }

    return deduped;
  });

  return {
    type: "Feature",
    properties: {},
    geometry: { type: "Polygon", coordinates: coords },
  };
};

/**
 * Extract all Polygons from a KML DOM document (count is validated by the caller).
 */
const extractPolygonsFromKML = (kmlDoc) => {
  const polygonElements = kmlDoc.getElementsByTagName("Polygon");
  const features = [];

  for (const polygonEl of polygonElements) {
    const coordinates = parseKMLPolygonCoordinates(polygonEl);
    if (!coordinates) continue;

    features.push(
      normaliseFeature({
        type: "Feature",
        properties: {},
        geometry: { type: "Polygon", coordinates },
      }),
    );
  }

  return features;
};

const parseKMLPolygonCoordinates = (polygonEl) => {
  const rings = [];

  const outerBoundary = polygonEl.getElementsByTagName("outerBoundaryIs")[0];
  if (!outerBoundary) return null;

  const outerCoords = parseKMLCoordinateString(
    outerBoundary.getElementsByTagName("coordinates")[0]?.textContent,
  );
  if (!outerCoords) return null;
  rings.push(outerCoords);

  const innerBoundaries = polygonEl.getElementsByTagName("innerBoundaryIs");
  for (const inner of innerBoundaries) {
    const innerCoords = parseKMLCoordinateString(
      inner.getElementsByTagName("coordinates")[0]?.textContent,
    );
    if (innerCoords) rings.push(innerCoords);
  }

  return rings;
};

const parseKMLCoordinateString = (coordString) => {
  if (!coordString) return null;

  const coords = coordString
    .trim()
    .split(/\s+/)
    .map((pair) => {
      const parts = pair.split(",").map(Number);
      if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
      return [parts[0], parts[1]];
    })
    .filter(Boolean);

  return coords.length >= 3 ? coords : null;
};